/**
 * Model szablonu wiadomości e-mail.
 *
 * Wiadomość to LISTA BLOKÓW UŁOŻONYCH JEDEN POD DRUGIM, a nie swobodna plansza jak w
 * generatorze reklam. Klienty pocztowe (przede wszystkim Outlook) nie renderują
 * pozycjonowania absolutnego ani flexboxa, więc jedyny układ, który zachowuje się
 * przewidywalnie wszędzie, to tabela z kolejnymi wierszami. Model odwzorowuje wprost to,
 * co da się bezpiecznie wyrenderować.
 */

export type EmailBlockKind =
  | 'logo'
  | 'heading'
  | 'text'
  | 'button'
  | 'image'
  | 'list'
  | 'divider'
  | 'spacer'
  | 'footer';

export type EmailAlign = 'left' | 'center' | 'right';

interface BaseBlock {
  id: string;
  kind: EmailBlockKind;
}

export interface LogoBlock extends BaseBlock {
  kind: 'logo';
  /** Adres pliku. Domyślnie logo hotelspot z Firebase Storage, bo poczta potrzebuje URL. */
  src: string;
  alt: string;
  /** Wysokość w pikselach; szerokość dobiera się sama. */
  height: number;
  align: EmailAlign;
}

export interface HeadingBlock extends BaseBlock {
  kind: 'heading';
  text: string;
  level: 1 | 2;
  align: EmailAlign;
}

export interface TextBlock extends BaseBlock {
  kind: 'text';
  text: string;
  align: EmailAlign;
  /** Mniejszy stopień pisma dla dopisków pod treścią właściwą. */
  small: boolean;
}

export interface ButtonBlock extends BaseBlock {
  kind: 'button';
  label: string;
  href: string;
  align: EmailAlign;
  /** Przycisk na całą szerokość kolumny czyta się lepiej na telefonie. */
  fullWidth: boolean;
}

export interface ImageBlock extends BaseBlock {
  kind: 'image';
  src: string;
  /** Tekst alternatywny. Grafiki bywają domyślnie blokowane, więc to on niesie treść. */
  alt: string;
  href: string;
  /** Zaokrąglenie rogów grafiki. Outlook je zignoruje i pokaże prostokąt. */
  radius: number;
}

export interface ListBlock extends BaseBlock {
  kind: 'list';
  items: string[];
}

export interface DividerBlock extends BaseBlock {
  kind: 'divider';
}

export interface SpacerBlock extends BaseBlock {
  kind: 'spacer';
  height: number;
}

export interface FooterBlock extends BaseBlock {
  kind: 'footer';
  text: string;
  /** Link wypisania z listy. Pusty adres oznacza, że stopka go nie pokazuje. */
  unsubscribeUrl: string;
  unsubscribeLabel: string;
}

export type EmailBlock =
  | LogoBlock
  | HeadingBlock
  | TextBlock
  | ButtonBlock
  | ImageBlock
  | ListBlock
  | DividerBlock
  | SpacerBlock
  | FooterBlock;

export interface EmailTheme {
  /** Kolor akcentu: przyciski i linki. */
  brand: string;
  /** Tło poza kartą wiadomości. */
  background: string;
  /** Tło samej karty. */
  surface: string;
  text: string;
  muted: string;
  border: string;
  fontFamily: string;
  /** Zaokrąglenie karty i przycisków. Outlook je ignoruje i to nie jest błąd. */
  radius: number;
  /** Szerokość karty w pikselach. 600 to bezpieczny standard skrzynek. */
  width: number;
}

export interface EmailDocument {
  /** Nazwa robocza szablonu, widoczna tylko w panelu. */
  name: string;
  /** Temat wiadomości. */
  subject: string;
  /** Zajawka pokazywana na liście wiadomości obok tematu. */
  preheader: string;
  theme: EmailTheme;
  blocks: EmailBlock[];
}

/*
 * Motyw domyślny jest CZARNO-BIAŁY I BEZ KARTY: białe tło, czarny tekst i przycisk,
 * a strukturę robią nagłówki i cienkie linie. Kolorowe pudełko na szarym tle wygląda jak
 * newsletter marketingowy i w skrzynce administratora czyta się gorzej niż zwykły list.
 */
export const DEFAULT_THEME: EmailTheme = {
  brand: '#111111',
  background: '#ffffff',
  surface: '#ffffff',
  text: '#111111',
  muted: '#6b7280',
  border: '#e5e7eb',
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
  radius: 0,
  width: 600,
};

/**
 * Logo hotelspot w wersji PNG spod publicznego adresu. Ten sam plik, którego używają
 * wiadomości transakcyjne z backendu: poczta nie zna paczkowanych zasobów, a SVG bywa
 * blokowane (m.in. przez Gmaila), więc wchodzi rastrowy plik spod stałego URL-a.
 */
export const HOTELSPOT_LOGO_URL = 'https://firebasestorage.googleapis.com/v0/b/hotelspot-dev.appspot.com/o/'
  + 'logo-hotelspot.png?alt=media&token=ff09a2d3-ccab-4504-8c14-4ddf40c2a232';

/**
 * Pola podstawiane przez backend przy wysyłce. Znacznik spoza tej listy zostaje w treści
 * dosłownie, więc odbiorca zobaczyłby w mailu surowe `{{...}}`.
 *
 * Pola podstawiane przy wysyłce. W kreatorze zostają w treści jako zwykły tekst. */
export const EMAIL_PLACEHOLDERS = [
  { token: '{{firstName}}', label: 'Imię odbiorcy' },
  { token: '{{hotelName}}', label: 'Nazwa hotelu' },
  { token: '{{panelUrl}}', label: 'Adres panelu' },
  { token: '{{unsubscribeUrl}}', label: 'Adres wypisania z listy' },
] as const;
